import { z } from 'zod';

/**
 * Provider-aware account schema.
 * - IMAP accounts require the usual IMAP/SMTP fields.
 * - Hostinger accounts only need email/display name + a Bearer token
 *   (token is write-only: required on create, optional on edit — blank keeps the stored one).
 */
export const buildEmailAccountSchema = (isEdit = false) =>
  z.object({
    provider: z.enum(['imap', 'hostinger']).default('imap'),
    email_address: z.string().email('Invalid email address').min(1, 'Email address is required'),
    display_name: z.string().min(1, 'Display name is required').max(100, 'Display name too long'),
    signature_html: z.string().optional(),

    // Hostinger (write-only token)
    hostinger_token: z.string().optional(),

    // IMAP Settings (optional at base level — enforced by superRefine for IMAP accounts)
    imap_server: z.string().optional(),
    imap_port: z.number().int().min(1, 'Invalid port').max(65535, 'Invalid port').default(993),
    imap_username: z.string().optional(),
    imap_password: z.string().optional(),
    imap_mailbox: z.string().default('INBOX'),

    // SMTP Settings
    smtp_server: z.string().optional(),
    smtp_port: z.number().int().min(1, 'Invalid port').max(65535, 'Invalid port').default(587),
    smtp_username: z.string().optional(),
    smtp_password: z.string().optional(),

    // Advanced Settings
    use_ssl: z.boolean().default(true),
    trust_self_signed: z.boolean().default(false),
    max_sync_per_run: z.number().int().min(1, 'Must be at least 1').max(1000, 'Cannot exceed 1000').default(200),
    sync_window_days: z.number().int().min(1, 'Must be at least 1 day').max(365, 'Cannot exceed 365 days').default(30),
    sync_delete_email_from_server: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.provider === 'hostinger') {
      if (!isEdit && !data.hostinger_token?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['hostinger_token'],
          message: 'Hostinger API token is required',
        });
      }
      return;
    }

    const requiredStrings: Array<[keyof typeof data, string]> = [
      ['imap_server', 'IMAP server is required'],
      ['imap_username', 'IMAP username is required'],
      ['imap_password', 'IMAP password is required'],
      ['imap_mailbox', 'IMAP mailbox is required'],
      ['smtp_server', 'SMTP server is required'],
      ['smtp_username', 'SMTP username is required'],
      ['smtp_password', 'SMTP password is required'],
    ];
    for (const [field, message] of requiredStrings) {
      const value = data[field];
      if (typeof value !== 'string' || !value.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
      }
    }
  });

export const emailAccountSchema = buildEmailAccountSchema(false);

export type EmailAccountFormData = z.infer<ReturnType<typeof buildEmailAccountSchema>>;
